import React, { useState } from 'react';
import { Form, Input, Button, message, Checkbox } from 'antd';
import { EyeInvisibleOutlined, EyeTwoTone, MailOutlined, LockOutlined, UserOutlined } from '@ant-design/icons';
import { Shield, BarChart3, TrendingUp, Users, CreditCard, Zap, Globe, Award, Clock } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';

const RegisterPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [form] = Form.useForm();
  const { register: registerUser } = useAuth();
  const navigate = useNavigate();

  const onFinish = async (values) => {
    setIsLoading(true);
    try {
      await registerUser({
        name: values.name,
        email: values.email,
        password: values.password
      });
      message.success('Registration successful! Please check your email for verification.');
      navigate('/login');
    } catch (error) {
      message.error(error.response?.data?.message || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 lg:bg-white flex">
      {/* Left Side - Form (Desktop) / Full Screen (Mobile) */}
      <div className="w-full lg:w-1/2 flex flex-col min-h-screen">
        {/* Mobile Header */}
        <div className="lg:hidden bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 relative overflow-hidden">
          {/* Mobile Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="mobile-register-dots" x="0" y="0" width="30" height="30" patternUnits="userSpaceOnUse">
                  <circle cx="15" cy="15" r="1.5" fill="white" opacity="0.4"/>
                  <circle cx="30" cy="30" r="1" fill="white" opacity="0.2"/>
                  <circle cx="0" cy="0" r="0.8" fill="white" opacity="0.1"/>
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#mobile-register-dots)"/>
            </svg>
          </div>

          {/* Mobile decorative elements */}
          <div className="absolute top-5 right-5 w-20 h-20 bg-white opacity-5 rounded-full blur-2xl"></div>
          <div className="absolute bottom-5 left-5 w-24 h-24 bg-gray-400 opacity-8 rounded-full blur-2xl"></div>

          <div className="relative z-10 pt-12 pb-8 text-center px-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl mb-4 shadow-xl">
              <div className="text-2xl font-bold text-gray-800">$</div>
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Divya Enterprise</h1>
            <p className="text-gray-300 text-lg">Business Account Manager</p>
          </div>
        </div>

        {/* Form Container */}
        <div className="flex-1 bg-white lg:bg-gray-50 relative">
          {/* Mobile curved overlay effect */}
          <div className="lg:hidden absolute top-0 left-0 right-0 h-8 bg-white rounded-t-3xl -mt-6 relative z-10"></div>
          
          <div className="lg:flex lg:items-center lg:justify-center lg:min-h-screen lg:px-12">
            <div className="w-full max-w-md lg:max-w-lg bg-white lg:rounded-2xl lg:shadow-2xl lg:border lg:border-gray-200 p-8 lg:p-12 relative z-20">
              {/* Desktop Header */}
              <div className="hidden lg:block text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-800 rounded-2xl mb-4">
                  <div className="text-2xl font-bold text-white">$</div>
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Create Account</h2>
                <p className="text-gray-600">Join thousands of businesses worldwide</p>
              </div>

              {/* Mobile Header */}
              <div className="lg:hidden mb-8 pt-4">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Create Account</h2>
                <p className="text-gray-600">Join thousands of businesses worldwide</p>
              </div>

              {/* Register Form */}
              <Form
                form={form}
                name="register"
                onFinish={onFinish}
                layout="vertical"
                size="large"
                className="register-form"
              >
                {/* Name Field */}
                <Form.Item
                  name="name"
                  rules={[
                    { required: true, message: 'Name is required' },
                    { min: 2, message: 'Name must be at least 2 characters' }
                  ]}
                  className="mb-6"
                >
                  <Input
                    prefix={<UserOutlined className="text-gray-400 mr-2" />}
                    placeholder="Full Name"
                    className="custom-input"
                  />
                </Form.Item>

                {/* Email Field */}
                <Form.Item
                  name="email"
                  rules={[
                    { required: true, message: 'Email is required' },
                    { type: 'email', message: 'Invalid email address' }
                  ]}
                  className="mb-6"
                >
                  <Input
                    prefix={<MailOutlined className="text-gray-400 mr-2" />}
                    placeholder="Email Address"
                    className="custom-input"
                  />
                </Form.Item>

                {/* Password Field */}
                <Form.Item
                  name="password"
                  rules={[
                    { required: true, message: 'Password is required' },
                    { min: 6, message: 'Password must be at least 6 characters' }
                  ]}
                  className="mb-6"
                >
                  <Input.Password
                    prefix={<LockOutlined className="text-gray-400 mr-2" />}
                    placeholder="Password"
                    iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                    className="custom-input"
                  />
                </Form.Item>

                {/* Confirm Password Field */}
                <Form.Item
                  name="confirmPassword"
                  dependencies={['password']}
                  rules={[
                    { required: true, message: 'Please confirm your password' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('password') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('Passwords do not match'));
                      },
                    }),
                  ]}
                  className="mb-6"
                >
                  <Input.Password
                    prefix={<LockOutlined className="text-gray-400 mr-2" />}
                    placeholder="Confirm Password"
                    iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                    className="custom-input"
                  />
                </Form.Item>

                {/* Terms and Conditions */}
                <Form.Item
                  name="terms"
                  valuePropName="checked"
                  rules={[
                    { required: true, message: 'Please agree to the terms and conditions' }
                  ]}
                  className="mb-6"
                >
                  <Checkbox className="text-gray-600">
                    I agree to the{' '}
                    <Link to="/terms" className="text-gray-800 hover:text-gray-900 font-semibold">
                      Terms & Conditions
                    </Link>
                  </Checkbox>
                </Form.Item>

                {/* Register Button */}
                <Form.Item className="mb-6">
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={isLoading}
                    className="custom-button w-full"
                  >
                    {!isLoading && (
                      <>
                        Create Account
                      </>
                    )}
                  </Button>
                </Form.Item>
              </Form>

              {/* Login Link */}
              <div className="text-center mb-8">
                <span className="text-gray-600">Already have an account? </span>
                <Link to="/login" className="text-gray-800 font-semibold hover:text-gray-900">
                  Sign In
                </Link>
              </div>

              {/* Features Row */}
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mb-2">
                    <Shield className="w-5 h-5 text-gray-600" />
                  </div>
                  <span className="text-sm text-gray-600 font-medium">Secure</span>
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mb-2">
                    <BarChart3 className="w-5 h-5 text-gray-600" />
                  </div>
                  <span className="text-sm text-gray-600 font-medium">Analytics</span>
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mb-2">
                    <TrendingUp className="w-5 h-5 text-gray-600" />
                  </div>
                  <span className="text-sm text-gray-600 font-medium">Growth</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-white border-t border-gray-200 py-4 text-center">
          <p className="text-gray-500 text-sm">© 2025 Divya Enterprise. All rights reserved.</p>
        </div>
      </div>

      {/* Desktop Right Side - Benefits & Features (SAME GRAY COLOR SCHEME) */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="register-dots" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                <circle cx="20" cy="20" r="2" fill="white" opacity="0.3"/>
                <circle cx="40" cy="40" r="1.5" fill="white" opacity="0.2"/>
                <circle cx="0" cy="0" r="1" fill="white" opacity="0.1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#register-dots)"/>
          </svg>
        </div>

        {/* Cloud-like decorative elements */}
        <div className="absolute top-16 left-16 w-32 h-32 bg-white opacity-5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-32 right-16 w-40 h-40 bg-gray-400 opacity-10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/3 right-1/4 w-24 h-24 bg-white opacity-8 rounded-full blur-2xl"></div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 w-full">
          <div className="mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-3xl mb-6 shadow-2xl">
              <div className="text-3xl font-bold text-gray-800">$</div>
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">Join Divya Enterprise</h1>
            <p className="text-xl text-gray-300 mb-8">Start managing your business accounts like a pro</p>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-2 gap-6 mb-8">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center mr-4">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Team Collaboration</h3>
                <p className="text-gray-300 text-sm">Work together seamlessly</p>
              </div>
            </div>
            
            <div className="flex items-center">
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center mr-4">
                <CreditCard className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Smart Invoicing</h3>
                <p className="text-gray-300 text-sm">Automated billing system</p>
              </div>
            </div>

            <div className="flex items-center">
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center mr-4">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Real-time Analytics</h3>
                <p className="text-gray-300 text-sm">Live business insights</p>
              </div>
            </div>

            <div className="flex items-center">
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center mr-4">
                <Globe className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Cloud Access</h3>
                <p className="text-gray-300 text-sm">Anywhere, anytime access</p>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-6 border-t border-white border-opacity-20 pt-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-white mb-1">500+</div>
              <div className="text-gray-300 text-sm">Happy Clients</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white mb-1">99.9%</div>
              <div className="text-gray-300 text-sm">Uptime</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white mb-1">24/7</div>
              <div className="text-gray-300 text-sm">Support</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;